import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@monksflow/monks-ui';

export interface MetricCardProps {
  title: string;
  /** What the number means, e.g. "Total over the period". */
  description: string;
  /** Headline figure, already formatted. Omitted while loading. */
  value?: string;
  isLoading: boolean;
  isError: boolean;
  /** True when the series came back with nothing to plot. */
  isEmpty: boolean;
  children: ReactNode;
}

export const MetricCard = ({
  title,
  description,
  value,
  isLoading,
  isError,
  isEmpty,
  children,
}: MetricCardProps) => (
  // bg-transparent + a border, not the Card default: these sit inside a
  // content-block, which is itself an opaque bg-card surface, so keeping the
  // default fill would put the same colour on the same colour with only a faint
  // shadow between them. No hover treatment: these cards are not interactive.
  //
  // h-full + a flex column so the chart can absorb the leftover height: grid
  // rows stretch every card to the tallest one (the legend makes the messages
  // card taller), which otherwise leaves dead space under the shorter charts.
  //
  // `p-md`, not the `p-card` alias: tailwind-merge only displaces Card's own
  // `py-6` with a padding class built from a design-system step. The header and
  // content drop their `px-6` so the Card's padding is the only inset.
  <Card
    data-testid="metric-card"
    className="flex h-full flex-col gap-xl rounded-lg border border-border-light bg-transparent p-md shadow-none"
  >
    <CardHeader data-testid="metric-card-header" className="px-0">
      <CardTitle className="type-body-lg-strong text-foreground">{title}</CardTitle>
      <CardDescription className="type-body-md text-foreground-subtle">
        {description}
      </CardDescription>
    </CardHeader>
    <CardContent
      data-testid="metric-card-content"
      className="flex min-h-0 flex-1 flex-col gap-3 px-0"
    >
      <MetricBody
        value={value}
        isLoading={isLoading}
        isError={isError}
        isEmpty={isEmpty}
        title={title}
      >
        {children}
      </MetricBody>
    </CardContent>
  </Card>
);

const MetricBody = ({
  value,
  isLoading,
  isError,
  isEmpty,
  title,
  children,
}: Omit<MetricCardProps, 'description'>) => {
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <>
        <Skeleton className="h-9 w-24" />
        <Skeleton className="min-h-40 w-full flex-1" />
      </>
    );
  }

  // A failure here is one card's worth of data, not the page. Analytics is also
  // the one feature that can be flag-on while the backend has no credentials
  // (404), so this state has to read as "unavailable", not "broken app".
  if (isError) {
    return (
      <p className="flex min-h-40 flex-1 items-center justify-center text-center text-sm text-muted-foreground">
        {t('analyticsDashboard.metricCard.titleIsUnavailableRightNow', { title })}
      </p>
    );
  }

  if (isEmpty) {
    return (
      <p className="flex min-h-40 flex-1 items-center justify-center text-center text-sm text-muted-foreground">
        {t('analyticsDashboard.metricCard.noActivityInThisPeriod')}
      </p>
    );
  }

  return (
    <>
      <p className="text-3xl font-semibold tracking-tight text-foreground">{value}</p>
      {children}
    </>
  );
};
